import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import FilledButton from '@/components/FilledButton.jsx';

import { useSearchRestingState } from '@/hooks/use-search-resting-state.js';
import useProfessionalMappings from '@/hooks/useProfessionalMappings.js';

import { isXundEnabled } from '@/service/Xund/xundService.js';

import { DARK_HERO_BAND_HEIGHT, DARK_HERO_BAND_HEIGHT_MOBILE, onBandColor } from '@/styles/darkHero.ts';

import {
    XUND_RESULT_TYPE,
    XUND_SPECIALIZATION_TYPE,
    XUND_TO_TREATMENT_MAPPINGS_DEV,
    XUND_TO_TREATMENT_MAPPINGS_KFH,
} from '@/utils/constants.js';

import {
    CLEARED_CHAIN_SELECTION,
    useBookingStore,
    useConfigStore,
    usePersistedPrismicStore,
    useSearchStore,
    useXundStore,
} from '@/store.js';

import BookingModal from '../BookingModal.jsx';
import EternoSpinner from '../EternoLogoSpinner/eterno-spinner.tsx';
import XundIframe from '../XundIframe.jsx';
import LocationProfessionals from './LocationProfessionals.jsx';
import LocationTreatments from './LocationTreatments.jsx';
import SearchBar from './SearchBar.jsx';
import SearchResults from './SearchResults.jsx';

const useStyles = makeStyles((theme) => ({
    spinnerContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
    },
    bandHeader: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
        minHeight: DARK_HERO_BAND_HEIGHT,
        [theme.breakpoints.down('sm')]: {
            minHeight: DARK_HERO_BAND_HEIGHT_MOBILE,
        },
    },
    title: {
        width: '100%',
        marginBottom: '1rem',
        textAlign: 'center',
        '& > p': {
            fontSize: '2rem',
            fontWeight: theme.weight.heading,
            lineHeight: 1.125,
            color: onBandColor(theme),
        },
    },
    subtitle: {
        width: '100%',
        maxWidth: '32rem',
        marginBottom: '1.5rem',
        textAlign: 'center',
        '& > p': {
            fontSize: '1rem',
            fontWeight: theme.weight.body,
            color: onBandColor(theme),
        },
    },
    fullWidthButtonContainer: {
        width: '100%',
        marginTop: '20px',
        marginBottom: '20px',
    },
    fullWidthButton: {
        width: '100%',
    },
}));

const SearchTool = ({ showBrowse = true }) => {
    const classes = useStyles();
    const { i18n, t } = useTranslation();
    const {
        [i18n.language]: { dataSet },
    } = usePersistedPrismicStore((state) => state.bookAppointmentData);
    const locations = useSearchStore((state) => state.locations);
    const prismicData = useSearchStore((state) => state.prismicData);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const treatments = useSearchStore((state) => state.treatments);
    const showBookingModal = useBookingStore((state) => state.showBookingModal);
    const isXundTriage = useXundStore((state) => state.isXundTriage);
    const isXundTriageCompleted = useXundStore((state) => state.isXundTriageCompleted);
    const isXundTriageProcessed = useXundStore((state) => state.isXundTriageProcessed);

    const isRestingState = useSearchRestingState();

    // Local state for Xund results
    const [xundResult, setXundResult] = useState(null); // XUND_RESULT_TYPE.EMERGENCY, XUND_RESULT_TYPE.NO_TREATMENT, XUND_RESULT_TYPE.SUCCESS, null
    const [currentSessionId, setCurrentSessionId] = useState(null);

    const customer = useConfigStore.getState().currentCustomer;
    const customerId = customer?.customer_id?.[0].text;

    useProfessionalMappings();

    const handleXundToggle = () => {
        const newIsXundTriage = !isXundTriage;

        // Always clear local state first, especially when opening iframe
        setXundResult(null);

        // Generate new session ID when opening iframe
        if (newIsXundTriage) {
            setCurrentSessionId(Date.now());
        }

        useSearchStore.setState({
            // Clear search when opening Xund iframe
            search: '',
            selectedTreatments: {},
            ...CLEARED_CHAIN_SELECTION,
        });

        useXundStore.setState({
            isXundTriage: newIsXundTriage,
            isXundTriageCompleted: false,
            isXundTriageProcessed: false,
        });
    };

    const analyzeXundResults = (xundData) => {
        // Extract form data from polling response - handle nested API response structure
        const formData = xundData?.data?.form || xundData?.form || xundData; // Handle nested structure

        // Get the first illness from the Xund response
        const firstIllness = formData?.illnessCategories?.[0]?.illnesses?.[0];
        if (!firstIllness) {
            setXundResult(XUND_RESULT_TYPE.NO_TREATMENT);
            useXundStore.setState({ isXundTriageProcessed: true });
            return;
        }

        const { specializations } = firstIllness;

        // Priority 1: Check if FIRST specialization is HOSPITAL type (only first position triggers emergency)
        if (specializations.length > 0 && specializations[0].type === XUND_SPECIALIZATION_TYPE.HOSPITAL) {
            setXundResult(XUND_RESULT_TYPE.EMERGENCY);
            useXundStore.setState({ isXundTriageProcessed: true });
            return;
        }

        // Priority 2: Check if any DOCTOR specializations have mappings
        const locationTreatments = treatments[selectedLocation] ?? {};

        let hasMatchingTreatments = false;
        const selectedTreatmentsObj = {};

        const xundMapping =
            customerId === '294d6207-8d2d-447a-a2b3-54cb15af294c'
                ? XUND_TO_TREATMENT_MAPPINGS_KFH
                : XUND_TO_TREATMENT_MAPPINGS_DEV;

        for (const spec of specializations) {
            if (spec.type === XUND_SPECIALIZATION_TYPE.DOCTOR && xundMapping[spec.id]) {
                const treatmentKey = xundMapping[spec.id];

                // Check if this treatment exists in current location
                for (const treatment of Object.values(locationTreatments)) {
                    if (treatment.key[0].text === treatmentKey) {
                        hasMatchingTreatments = true;
                        selectedTreatmentsObj[treatmentKey] = true;
                    }
                }
            }
        }

        if (hasMatchingTreatments) {
            // Store selected treatments for later use
            useSearchStore.setState({ selectedTreatments: selectedTreatmentsObj, ...CLEARED_CHAIN_SELECTION });
            setXundResult(XUND_RESULT_TYPE.SUCCESS);
        } else {
            setXundResult(XUND_RESULT_TYPE.NO_TREATMENT);
        }
        useXundStore.setState({ isXundTriageProcessed: true });
    };

    const handleXundComplete = (xundResults) => {
        if (xundResults && xundResults.data?.status === 'completed') {
            // Analyze results to set button state
            analyzeXundResults(xundResults);
        }
    };

    const handleFinishTriageButtonClick = () => {
        useXundStore.setState({ isXundTriage: false, isXundTriageCompleted: true });

        // Clear the local result state after handling
        const currentResult = xundResult;
        setXundResult(null);

        if (currentResult === XUND_RESULT_TYPE.NO_TREATMENT) {
            // Show all treatments by selecting them all
            const locationTreatments = treatments[selectedLocation] ?? {};

            // Create a selectedTreatments object with all treatments set to true
            const allTreatmentsSelected = {};
            for (const treatment of Object.values(locationTreatments)) {
                allTreatmentsSelected[treatment.key[0].text] = true;
            }

            useSearchStore.setState({
                search: '',
                selectedTreatments: allTreatmentsSelected,
                ...CLEARED_CHAIN_SELECTION,
            });
        }
        // For 'success' case, selectedTreatments are already set in analyzeXundResults
        // For 'emergency' case, button is disabled so this won't be called
    };

    if (!prismicData || !locations || !dataSet) {
        return (
            <div className={classes.spinnerContainer}>
                <EternoSpinner />
            </div>
        );
    }

    // Get button text and color based on Xund result
    const getXundButtonProps = () => {
        switch (xundResult) {
            case XUND_RESULT_TYPE.EMERGENCY:
                return {
                    text: t('EmergencyMatch'),
                    color: 'red',
                };
            case XUND_RESULT_TYPE.NO_TREATMENT:
                return {
                    text: t('NoMatch'),
                };
            case XUND_RESULT_TYPE.SUCCESS:
                return {
                    text: t('AppointmentMatch'),
                };
            default:
                // After completion but before processing
                return {
                    text: t('AppointmentMatch'),
                };
        }
    };

    const xundButtonProps = getXundButtonProps();

    return (
        <>
            <div className={classes.bandHeader}>
                <div className={classes.title}>
                    <Typography>{t('BookAppointmentHeading')}</Typography>
                </div>
                {customer?.is_eterno_customer && (
                    <div className={classes.subtitle}>
                        <Typography>{t('BookAppointmentSubheading')}</Typography>
                    </div>
                )}
                <SearchBar showXund={isXundTriage} onXundToggle={handleXundToggle} />
            </div>
            {isXundEnabled(selectedLocation, customerId) && isXundTriageProcessed && !isXundTriageCompleted && (
                <div className={classes.fullWidthButtonContainer}>
                    <FilledButton
                        text={xundButtonProps.text}
                        color={xundButtonProps.color}
                        fullWidth
                        className={classes.fullWidthButton}
                        onPress={xundResult === XUND_RESULT_TYPE.EMERGENCY ? null : handleFinishTriageButtonClick}
                    />
                </div>
            )}
            {showBrowse && isRestingState && <LocationTreatments />}
            {showBrowse && isRestingState && <LocationProfessionals />}
            {(!isXundTriage || isXundTriageCompleted) && <SearchResults />}
            {isXundEnabled(selectedLocation, customerId) && (
                <XundIframe show={isXundTriage} onComplete={handleXundComplete} sessionId={currentSessionId} />
            )}
            {showBookingModal && <BookingModal />}
        </>
    );
};

export default SearchTool;
